@if (data(); as stats) {
    <div class="space-y-8 animate-in fade-in duration-700 h-full">

        <!-- Header Section -->
        <div class="flex items-center justify-between">
            <div class="space-y-1">
                <h2 class="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                    Analytics & Insights
                </h2>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                    Monitor token consumption and traffic distribution across your chatbot deployment
                </p>
            </div>

            @if (isLoading()) {
                <div class="flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
                    <app-icon-v2 name="refresh-ccw" [size]="16" class="animate-spin"></app-icon-v2>
                    <span class="text-xs font-semibold uppercase tracking-wider">Refreshing...</span>
                </div>
            }
        </div>

        <!-- Summary Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <!-- Total Tokens -->
            <div class="group relative overflow-hidden rounded-2xl border border-indigo-100 dark:border-indigo-500/20 bg-gradient-to-br from-indigo-50/50 via-white to-white dark:from-gray-900/90 dark:via-gray-900/60 dark:to-indigo-950/30 backdrop-blur-sm p-6 shadow-sm hover:shadow-md transition-all duration-300">
                <div class="absolute -right-4 -top-4 w-24 h-24 bg-indigo-500/10 dark:bg-indigo-500/20 rounded-full blur-2xl group-hover:bg-indigo-500/20 dark:group-hover:bg-white/10 transition-all"></div>
                <div class="relative space-y-4">
                    <div class="flex items-center justify-between">
                        <div class="p-2.5 rounded-xl bg-indigo-100 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 dark:ring-1 dark:ring-indigo-500/20">
                            <app-icon-v2 name="zap" [size]="20"></app-icon-v2>
                        </div>
                        <span class="text-[10px] font-bold uppercase tracking-widest text-indigo-400 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 px-2 py-1 rounded-full border border-indigo-100 dark:border-indigo-500/20">Life-time</span>
                    </div>
                    <div>
                        <p class="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">
                            {{ stats.chatbotTotalTokens | number }}
                        </p>
                        <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-tight">
                            Total Tokens
                        </p>
                    </div>
                </div>
            </div>

            <!-- Total Sessions -->
            <div class="group relative overflow-hidden rounded-2xl border border-emerald-100 dark:border-emerald-500/20 bg-gradient-to-br from-emerald-50/50 via-white to-white dark:from-gray-900/90 dark:via-gray-900/60 dark:to-emerald-950/30 backdrop-blur-sm p-6 shadow-sm hover:shadow-md transition-all duration-300">
                <div class="absolute -right-4 -top-4 w-24 h-24 bg-emerald-500/10 dark:bg-emerald-500/20 rounded-full blur-2xl group-hover:bg-emerald-500/20 dark:group-hover:bg-emerald-500/30 transition-all"></div>
                <div class="relative space-y-4">
                    <div class="flex items-center justify-between">
                        <div class="p-2.5 rounded-xl bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 dark:ring-1 dark:ring-emerald-500/20">
                            <app-icon-v2 name="monitor" [size]="20"></app-icon-v2>
                        </div>
                   </div>
                    <div>
                        <p class="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">
                            {{ totalSessions() | number }}
                        </p>
                        <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-tight">
                            Total Sessions
                        </p>
                    </div>
                </div>
            </div>

            <!-- User Messages -->
            <div class="group relative overflow-hidden rounded-2xl border border-amber-100 dark:border-amber-500/20 bg-gradient-to-br from-amber-50/50 via-white to-white dark:from-gray-900/90 dark:via-gray-900/60 dark:to-amber-950/30 backdrop-blur-sm p-6 shadow-sm hover:shadow-md transition-all duration-300">
                <div class="absolute -right-4 -top-4 w-24 h-24 bg-amber-500/10 dark:bg-amber-500/20 rounded-full blur-2xl group-hover:bg-amber-500/20 dark:group-hover:bg-amber-500/30 transition-all"></div>
                <div class="relative space-y-4">
                    <div class="flex items-center justify-between">
                        <div class="p-2.5 rounded-xl bg-amber-100 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 dark:ring-1 dark:ring-amber-500/20">
                            <app-icon-v2 name="message-square" [size]="20"></app-icon-v2>
                        </div>
                   </div>
                    <div>
                        <p class="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">
                            {{ stats.chatbotTotalUserMessages | number }}
                        </p>
                        <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-tight">
                            User Messages
                        </p>
                    </div>
                </div>
            </div>

            <!-- AI Responses -->
            <div class="group relative overflow-hidden rounded-2xl border border-purple-100 dark:border-purple-500/20 bg-gradient-to-br from-purple-50/50 via-white to-white dark:from-gray-900/90 dark:via-gray-900/60 dark:to-purple-950/30 backdrop-blur-sm p-6 shadow-sm hover:shadow-md transition-all duration-300">
                <div class="absolute -right-4 -top-4 w-24 h-24 bg-purple-500/10 dark:bg-purple-500/20 rounded-full blur-2xl group-hover:bg-purple-500/20 dark:group-hover:bg-white/10 transition-all"></div>
                <div class="relative space-y-4">
                    <div class="flex items-center justify-between">
                        <div class="p-2.5 rounded-xl bg-purple-100 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 dark:ring-1 dark:ring-purple-500/20">
                            <app-icon-v2 name="bot" [size]="20"></app-icon-v2>
                        </div>
                   </div>
                    <div>
                        <p class="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">
                            {{ stats.chatbotTotalAiResponses | number }}
                        </p>
                        <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-tight">
                            AI Responses
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sources Breakdown Table -->
        <div class="space-y-4">
            <div class="flex items-center justify-between px-2">
                <h3 class="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-widest">
                    Traffic & Engagement Breakdown
                </h3>
            </div>

            <div class="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800 bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl">
                <div class="overflow-x-auto">
                    <table class="w-full text-left border-collapse">
                        <thead>
                            <tr class="border-b border-gray-100 dark:border-gray-800/50 bg-gray-50/50 dark:bg-gray-900/50">
                                <th class="px-6 py-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest w-1/4">Source Platform</th>
                                <th class="px-6 py-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest text-center">Sessions</th>
                                <th class="px-6 py-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest text-center">User Messages</th>
                                <th class="px-6 py-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest text-center">AI Responses</th>
                                <th class="px-6 py-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Token Distribution</th>
                                <th class="px-6 py-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest text-right">Tokens</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-100 dark:divide-gray-800/50">
                            @for (source of stats.sources; track source.source) {
                                <tr class="group hover:bg-gray-50/80 dark:hover:bg-white/5 transition-colors cursor-pointer"
                                    (click)="openSessionBrowser(source); $event.stopPropagation()">
                                    <td class="px-6 py-5">
                                        <div class="flex items-center gap-4">
                                            <div class="shrink-0 flex items-center justify-center w-10 h-10 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm group-hover:scale-105 group-hover:border-indigo-400 dark:group-hover:border-line-strong transition-all duration-300">
                                                <app-icon-v2 [name]="source.source === 'prism' ? 'shield-check' : 'globe'" 
                                                           [size]="18" 
                                                           [className]="source.source === 'prism' ? 'text-indigo-500' : 'text-purple-500'">
                                                </app-icon-v2>
                                            </div>
                                            <div class="flex flex-col">
                                                <div class="flex items-center gap-2">
                                                    <span class="text-sm font-bold text-gray-900 dark:text-white capitalize">
                                                        {{ source.source || 'External / Global' }}
                                                    </span>
                                                    <app-icon-v2 name="arrow-up-right" [size]="12" class="text-gray-300 group-hover:text-indigo-500 transition-colors"></app-icon-v2>
                                                </div>
                                                <span class="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                                                    Last active: {{ source.lastActivityAt | date:'MMM d, h:mm a' }}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-5 text-center">
                                        <span class="inline-flex items-center px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-[11px] font-bold text-gray-700 dark:text-gray-300 tabular-nums">
                                            {{ source.sessionCount }}
                                        </span>
                                    </td>
                                    <td class="px-6 py-5 text-center">
                                        <span class="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                                            {{ source.totalUserMessages | number }}
                                        </span>
                                    </td>
                                    <td class="px-6 py-5 text-center">
                                        <span class="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                                            {{ source.totalAiResponses | number }}
                                        </span>
                                    </td>
                                    <td class="px-6 py-5">
                                        <div class="space-y-1.5 min-w-[140px]">
                                            <div class="flex items-center justify-between text-[10px]">
                                                <span class="font-bold text-indigo-600 dark:text-indigo-400 tabular-nums">
                                                    {{ getPercent(source.totalTokens, stats.chatbotTotalTokens) | number:'1.1-1' }}%
                                                </span>
                                            </div>
                                            <div class="h-1.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden border border-gray-200/50 dark:border-gray-700/50">
                                                <div class="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-1000 ease-out"
                                                     [style.width.%]="getPercent(source.totalTokens, stats.chatbotTotalTokens)">
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-5 text-right">
                                        <span class="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                                            {{ source.totalTokens | number }}
                                        </span>
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

    </div>
} @else if (!isLoading()) {
    <!-- Empty State -->
    <div class="flex flex-col items-center justify-center p-16 rounded-3xl border-2 border-dashed border-gray-200 dark:border-gray-800 bg-gray-50/30 dark:bg-gray-900/30 text-center animate-in zoom-in-95 duration-500">
        <div class="w-16 h-16 rounded-2xl bg-white dark:bg-gray-800 shadow-xl flex items-center justify-center mb-6 ring-1 ring-gray-100 dark:ring-gray-700">
            <app-icon-v2 name="bar-chart-3" [size]="28" class="text-gray-400"></app-icon-v2>
        </div>
        <h3 class="text-lg font-bold text-gray-900 dark:text-white">No data available yet</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 max-w-sm mt-2">
            When your chatbot starts interacting with users, detailed analytics and token usage insights will appear here.
        </p>
    </div>
} @else {
    <!-- Skeleton Loading -->
    <div class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            @for (i of [1,2,3]; track i) {
                <div class="h-32 rounded-2xl bg-gray-100 dark:bg-gray-800/50 animate-pulse"></div>
            }
        </div>
        <div class="h-80 rounded-2xl bg-gray-100 dark:bg-gray-800/50 animate-pulse"></div>
    </div>
}